/** @jsxImportSource @emotion/react */
import { css, keyframes } from "@emotion/react";
import { SelectChangeEvent, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { withMyTheme } from "../../theme/theme";
import { isMobile, mobileCss } from "../../util/isMobile";
import { useIsMobile } from "../../util/useIsMobile";
import { FeatureData, getFeatureData } from "./FeatureList";
import { FeatureSelect } from "./FeatureSelect";
import { FeatureSelectButtons } from "./FeatureSelectButtons";
import { LaptopContainer } from "./LaptopContainer";
import { LiveDemoButton } from "./LiveDemoButton";
import { openUrl } from "../../util/openUrl";

export const FEATURES_ID = "features";

export const FEATURES_BASE_URL = "https://live-demo.weddie.site";

const SectionContainerStyle = withMyTheme((theme) => css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6rem 2rem;
  background-color: ${theme.palette.background.default};
  position: relative;
  overflow: hidden;
  min-height: 80vh;
  
  ${mobileCss(`
    padding: 4rem 1rem;
  `)}
`);

const ContentContainerStyle = withMyTheme(() => css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 2rem;
  
  ${mobileCss(`
    flex-direction: column;
    margin-top: 0;
  `)}
`);

const MobileFeatureContentStyle = withMyTheme(() => css`
  display: none;
  ${mobileCss(`
    display: block;
    width: 90%;
    margin: 2vh auto 0vh auto;
    text-align: center;
  `)}
`);

const LeftColumnStyle = withMyTheme(() => css`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  
  ${mobileCss(`
    width: 100%;
    margin-bottom: 0rem;
  `)}
`);

const RightColumnStyle = withMyTheme(() => css`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0 3rem;
  align-items: center;
  
  ${mobileCss(`
    width: 100%;
    padding: 0 1rem;
  `)}
`);

const MainTitleStyle = withMyTheme((theme) => css`
  font-family: 'Great Vibes', serif;
  font-size: 3.5rem;
  font-family: ${theme.typography.h1.fontFamily};
  color: ${theme.palette.primary.main};
  margin-bottom: 1.5rem;
  text-align: center;
  
  ${mobileCss(`
    font-size: 2.8rem;
    margin-bottom: 1rem;
  `)}
`);

const TitleStyle = withMyTheme((theme) => css`
  font-family: 'Great Vibes', serif;
  font-size: 4vh;
  color: ${theme.palette.secondary.main};
  margin-bottom: 1vh;
  font-family: ${theme.typography.h1.fontFamily};
  
  ${mobileCss(`
    font-size: 2.5vh;
    color: ${theme.palette.secondary.main};
    text-align: center;
    margin-bottom: 0.5vh;
  `)}
`);

const MainDescriptionStyle = withMyTheme((theme) => css`
  font-family: 'Cormorant', serif;
  font-size: 1.6rem;
  color: ${theme.palette.text.primary};
  line-height: 1.6;
  margin-bottom: 2.5rem;
  text-align: center;
  max-width: 900px;
  
  ${mobileCss(`
    font-size: 1.3rem;
    margin-bottom: 2rem;
  `)}
`);

const DescriptionStyle = withMyTheme((theme) => css`
  font-family: 'Cormorant', serif;
  font-size: 1.4rem;
  color: ${theme.palette.text.primary};
  line-height: 1.6;
  margin-bottom: 1.5rem;
  
  ${mobileCss(`
    font-size: 1.2rem;
    text-align: center;
  `)}
`);

const HighlightStyle = withMyTheme((theme) => css`
  .highlight {
    color: ${theme.palette.primary.main};
    font-weight: 600;
  }
`);

const fadeInContent = keyframes`
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const FeatureContentStyle = withMyTheme(() => css`
  min-height: 200px;
  animation: ${fadeInContent} 0.5s ease-out;
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  ${mobileCss(`
    display: none;
  `)}
`);

const FeaturesTitleStyle = withMyTheme((theme) => css`
  font-family: 'Cormorant', serif;
  font-size: 7vh;
  font-weight: 600;
  color: ${theme.palette.primary.main};
  margin-top: 7vh;
  margin-bottom: 2vh;
  text-align: center;
  ${mobileCss(`
    font-size: 4vh;
    margin-top: 0.8vh;
    margin-bottom: 1rem;
  `)}
`);

export const Features = () => {
  const { t } = useTranslation();
  const isMobileView = useIsMobile();
  const featureData = getFeatureData(t);
  const [selectedFeature, setSelectedFeature] = useState<FeatureData>(featureData[0]);
  const [isLoading, setIsLoading] = useState(false);

  const openLiveDemo = () => {
    openUrl(FEATURES_BASE_URL)
  };

  // Keep track of which button was clicked for animation
  const [clickedButtonId, setClickedButtonId] = useState<string | null>(null);

  const handleFeatureChange = (feature: typeof featureData[0]) => {
    // Don't do anything if the same feature is clicked again
    if (selectedFeature.id === feature.id) return;

    // Set the clicked button for animation (only for desktop view)
    if (!isMobileView) {
      setClickedButtonId(feature.id);

      // Remove the clicked class after animation completes
      setTimeout(() => {
        setClickedButtonId(null);
      }, 1000);
    }

    // Show loading indicator
    setIsLoading(true);

    // Hide loading indicator after a delay
    setTimeout(() => {
      setIsLoading(false);
    }, 800);

    // Update selected feature
    setSelectedFeature(feature);
  }

  const handleFeatureClick = (feature: typeof featureData[0]) => {
    handleFeatureChange(feature);
  }

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const featureId = event.target.value;
    const feature = featureData.find(f => f.id === featureId);
    if (feature) {
      handleFeatureChange(feature);
    }
  };

  return (
    <div css={SectionContainerStyle} id={FEATURES_ID}>
      {/* Header section with title and description */}
      <Typography variant="h2" css={MainTitleStyle}>
        {t('features.title')}
      </Typography>

      {isMobile() && <LiveDemoButton openLiveDemo={openLiveDemo} />}

      <Typography
        css={[MainDescriptionStyle, HighlightStyle]}
        dangerouslySetInnerHTML={{
          __html: t('features.description')
            .replace('<highlight>', `<span class="highlight">`)
            .replace('</highlight>', '</span>')
        }}
      />
      {!isMobile() && <LiveDemoButton openLiveDemo={openLiveDemo} />}

      <Typography css={FeaturesTitleStyle}>
        {t('features.featuresTitle')}
      </Typography>
      <FeatureSelect selectedFeature={selectedFeature} handleSelectChange={handleSelectChange} featureData={featureData} />

      {/* Mobile feature content - shown above laptop */}
      <div css={MobileFeatureContentStyle}>
        <FeatureContent selectedFeature={selectedFeature} />
      </div>

      {/* Main content in row layout */}
      <div css={ContentContainerStyle}>
        <div css={LeftColumnStyle}>
          <LaptopContainer isLoading={isLoading} feature={selectedFeature} />
        </div>

        <div css={RightColumnStyle}>
          {!isMobile() &&
            <FeatureSelectButtons featureData={featureData} selectedFeature={selectedFeature} clickedButtonId={clickedButtonId} handleFeatureClick={handleFeatureClick} />
          }
          <div css={FeatureContentStyle}>
            <FeatureContent selectedFeature={selectedFeature} />
          </div>
        </div>
      </div>
    </div>
  );
};

const FeatureContent = ({ selectedFeature }: { selectedFeature: FeatureData }) => {
  return (
    <>
      <Typography css={TitleStyle}>
        {selectedFeature.title}
      </Typography>

      <Typography css={DescriptionStyle} dangerouslySetInnerHTML={{
        __html: selectedFeature.description
      }}>
      </Typography>
    </>
  );
};